/**
 * DeviceOrientation tilt source. Reads gamma (left/right rotation around the
 * y-axis) and maps it onto the steering-wheel target angle. iOS 13+ Safari
 * requires explicit permission, requested on first user gesture.
 */
const STORAGE_KEY = 'shmerge.tilt.enabled';

export type TiltStatus = 'idle' | 'unsupported' | 'pending' | 'granted' | 'denied';

export class DeviceTilt {
  private listener?: (e: DeviceOrientationEvent) => void;
  private currentGamma = 0;
  private status: TiltStatus = 'idle';
  private onStatusChange?: (status: TiltStatus) => void;

  isSupported(): boolean {
    return typeof window !== 'undefined' && 'DeviceOrientationEvent' in window;
  }

  isEnabled(): boolean {
    return this.status === 'granted';
  }

  getStatus(): TiltStatus {
    return this.status;
  }

  /** -1..+1 normalized tilt (gamma divided by 45°, clamped). */
  getNormalized(): number {
    if (!this.isEnabled()) return 0;
    const clamped = Math.max(-45, Math.min(45, this.currentGamma));
    return clamped / 45;
  }

  setOnStatusChange(fn: (status: TiltStatus) => void): void {
    this.onStatusChange = fn;
  }

  /** Persisted preference: should we auto-request on next boot? */
  static wantsAutoEnable(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private setStatus(s: TiltStatus): void {
    this.status = s;
    this.onStatusChange?.(s);
  }

  /** Must be called from a user gesture handler on iOS. */
  async requestPermission(): Promise<TiltStatus> {
    if (!this.isSupported()) {
      this.setStatus('unsupported');
      return this.status;
    }
    this.setStatus('pending');
    const DOE = window.DeviceOrientationEvent as typeof DeviceOrientationEvent & {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    let granted = true;
    if (typeof DOE.requestPermission === 'function') {
      try {
        const result = await DOE.requestPermission();
        granted = result === 'granted';
      } catch {
        granted = false;
      }
    }
    if (!granted) {
      this.setStatus('denied');
      try {
        localStorage.setItem(STORAGE_KEY, '0');
      } catch {}
      return this.status;
    }
    this.attach();
    // The API surface exists in Chrome desktop but no sensor events arrive
    // on a machine without an accelerometer. Confirm by waiting for at least
    // one event with usable gamma; otherwise downgrade to 'unsupported'.
    const got = await this.waitForFirstEvent(1500);
    if (!got) {
      this.detach();
      this.setStatus('unsupported');
      try {
        localStorage.setItem(STORAGE_KEY, '0');
      } catch {}
      return this.status;
    }
    this.setStatus('granted');
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {}
    return this.status;
  }

  private waitForFirstEvent(timeoutMs: number): Promise<boolean> {
    return new Promise((resolve) => {
      let done = false;
      const finish = (ok: boolean) => {
        if (done) return;
        done = true;
        window.removeEventListener('deviceorientation', probe);
        resolve(ok);
      };
      const probe = (e: DeviceOrientationEvent) => {
        if (typeof e.gamma === 'number' || typeof e.beta === 'number') finish(true);
      };
      window.addEventListener('deviceorientation', probe);
      setTimeout(() => finish(false), timeoutMs);
    });
  }

  disable(): void {
    this.detach();
    this.currentGamma = 0;
    this.setStatus('idle');
    try {
      localStorage.setItem(STORAGE_KEY, '0');
    } catch {}
  }

  private attach(): void {
    if (this.listener) return;
    this.listener = (e: DeviceOrientationEvent) => {
      if (typeof e.gamma === 'number') this.currentGamma = e.gamma;
    };
    window.addEventListener('deviceorientation', this.listener);
  }

  private detach(): void {
    if (!this.listener) return;
    window.removeEventListener('deviceorientation', this.listener);
    this.listener = undefined;
  }
}
